import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-charts/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-tree-view/themeAugmentation';
import * as React from 'react';
import { Outlet } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppNavbar from './AppNavbar';
import Header from './Header';
import SideMenu from './SideMenu';
import { HeaderTitleProvider } from './HeaderTitleContext';
import AppTheme from '../theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function DashboardLayout(props: { disableCustomTheme?: boolean }) {
  const [menuCollapsed, setMenuCollapsed] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (menuCollapsed) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target)) {
        // Don't collapse when clicking inside a dropdown/menu (e.g. OptionsMenu Logout)
        const isInsideMenu = (target as HTMLElement).closest?.('[role="menu"]');
        if (!isInsideMenu) {
          setMenuCollapsed(true);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuCollapsed]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <HeaderTitleProvider>
        <Box sx={{ display: 'flex' }}>
          <SideMenu
            collapsed={menuCollapsed}
            onExpand={() => setMenuCollapsed(false)}
            menuRef={menuRef}
          />
          <AppNavbar />
          <Box
            component="main"
            sx={(theme) => ({
              flexGrow: 1,
              backgroundColor: theme.vars
                ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                : alpha(theme.palette.background.default, 1),
              overflow: 'auto',
            })}
          >
            <Stack
              spacing={2}
              sx={{
                alignItems: 'center',
                px: '32px',
                pb: 5,
                mt: { xs: 8, md: 0 },
              }}
            >
              <Header />
              <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
                <PageTransition>
                  <Outlet />
                </PageTransition>
              </Box>
            </Stack>
          </Box>
        </Box>
      </HeaderTitleProvider>
    </AppTheme>
  );
}
